@if (this.incidentsPageStore.incident()) {
  <div class="header" [class.full-width]="isMergeView" #navigationEl>
    <!-- Details -->
    <section class="header-details">
      <div class="title-with-view-selector">
        <h1 class="h1-medium">Incident ID: {{ this.incidentsPageStore.incident()?.id }}</h1>

        <!-- View selector -->
        @if (this.incidentsPageStore.isEditMode()) {
          <span class="view-separator">/</span>
          <button
            [matMenuTriggerFor]="viewMenu"
            class="view-selector-button"
            [disabled]="(this.incidentsPageStore.incident()?.incidentDuplicateIds?.length ?? 0) === 0"
          >
            {{ isMergeView ? 'Merge view' : 'Edit view' }}
            @if ((this.incidentsPageStore.incident()?.incidentDuplicateIds?.length ?? 0) > 0) {
              <mat-icon>keyboard_arrow_down</mat-icon>
            }
          </button>

          <mat-menu #viewMenu="matMenu" class="options-menu-buttons">
            <button mat-menu-item (click)="setView(false)">Edit view</button>
            <button mat-menu-item (click)="setView(true)">Merge view</button>
          </mat-menu>
        }
      </div>

      <!-- Admin Flags and Actions -->
      @if (user?.roles?.includes(userRole.Admin)) {
        <!-- Incident Flags -->
        @if (this.incidentsPageStore.incident()?.isMasterIncident) {
          <div class="merged-incident-flag">Merged</div>
        } @else if (this.incidentsPageStore.incident()?.isDuplicateIncident) {
          <div class="duplicate-incident-flag">Duplicate</div>
        }

        <!-- Action buttons -->
        @if (!this.incidentsPageStore.isPending()) {
          <div class="action-buttons-group">
            @if (!this.incidentsPageStore.isEditMode()) {
              <button
                id="toggle-incident-is-disabled-button"
                class="button-secondary"
                mat-flat-button
                [disabled]="this.incidentsPageStore.incident()?.isDuplicateIncident"
                (click)="toggleIncidentIsDisabledAsync()"
              >
                {{ this.incidentsPageStore.incident()!.status === IncidentStatus.Disabled ? 'Restore' : 'Delete' }}
              </button>
              <button
                id="enter-edit-mode-button"
                class="button-primary"
                mat-flat-button
                [disabled]="this.incidentsPageStore.incident()?.isDuplicateIncident"
                (click)="enterEditMode()"
              >
                Edit
              </button>
            } @else {
              @if (this.isCreateMode) {
                <button id="delete-button" class="button-secondary" mat-flat-button (click)="navigateBack()">
                  Delete
                </button>
                <button
                  id="create-incident-button"
                  class="button-primary"
                  mat-flat-button
                  (click)="createIncidentAsync()"
                >
                  Create Incident
                </button>
              } @else {
                <button id="discard-button" class="button-secondary" mat-flat-button (click)="exitEditMode()">
                  Discard
                </button>
                @if (this.incidentsPageStore.incident()?.status === IncidentStatus.Active) {
                  <button
                    id="add-duplicate-incident-button"
                    class="button-secondary"
                    mat-flat-button
                    (click)="onAddDuplicateAsync()"
                  >
                    Add duplicate
                  </button>
                }
                <button id="create-incident-button" class="button-primary" mat-flat-button (click)="updateIncident()">
                  Save
                </button>
              }
            }
          </div>
        }
      }
    </section>

    <!-- Info banners -->
    <section class="header-info-banners">
      @if (user?.roles?.includes(userRole.Admin) && this.incidentsPageStore.incident()?.isDuplicateIncident) {
        <app-info-banner type="warning">
          <b>Editing disabled</b> <br />
          This incident must be detached from master incident
          <u>{{ this.incidentsPageStore.incident()?.masterIncidentId }}</u> before it can be edited.
          <a href="/incidents/{{ this.incidentsPageStore.incident()?.masterIncidentId }}"
            >View master incident <mat-icon>open_in_new</mat-icon></a
          >
        </app-info-banner>
      }

      @if (this.shouldShowValidationSummary && this.formGroup.invalid && this.incidentsPageStore.isEditMode()) {
        <app-info-banner type="error">
          <b>Missing required values: </b> <br />
          Complete the highlighted fields to continue:
          @for (fieldLabel of this.getMissingRequiredFieldLabels(); track fieldLabel; let isLast = $last) {
            <span class="missing-required-field">{{ fieldLabel }}</span
            >{{ isLast ? '.' : ', ' }}
          }
        </app-info-banner>
      }

      @if (this.incidentsPageStore.incident()?.status === IncidentStatus.Pending) {
        <app-info-banner type="warning">
          <b>Process pending:</b> <br />
          The incident will be generated overnight from the provided URL and available the next day.
        </app-info-banner>
      }
    </section>

    @if (!isMergeView) {
      <app-section-menu [menuItems]="sectionMenuItems" [scrollContainerId]="'content'" [scrollOffset]="250" />
    }
  </div>

  @if (!isMergeView) {
    <form [formGroup]="formGroup" class="main-form">
      <!-- Incident -->
      <section class="principal-details section-box" id="details">
        <app-incident-info [formGroup]="formGroup"> </app-incident-info>
      </section>

      <!-- Entity -->
      <section
        class="entity-details section-box"
        id="affected-entity"
        [class.section-disabled]="this.incidentsPageStore.isPending()"
      >
        <app-entity></app-entity>
      </section>

      <!-- Rims -->
      <section class="rims section-box" id="rims" [class.section-disabled]="this.incidentsPageStore.isPending()">
        <article class="rims-primary-details">
          <div class="section-header">
            <h4 class="descriptor body-large">RIMS</h4>
            <div class="section-header-title-section">
              <h1 class="title h1-medium">Technical insight</h1>
            </div>
          </div>

          <div [ngClass]="{ 'rims-grid': true }">
            @for (rimsPhaseInfo of this.rimsPhaseInfos; track $index) {
              <app-rims-phase-card
                [rimsPhaseInfo]="rimsPhaseInfo"
                [technicalSteps]="this.incidentsPageStore.incident()!.technicalSteps"
              />
            }
          </div>
        </article>
      </section>

      <!-- Threat Actor -->
      <section
        class="company-details section-box"
        id="threat-actor"
        [class.section-disabled]="this.incidentsPageStore.isPending()"
      >
        <app-threat-actor [formGroup]="formGroup"></app-threat-actor>
      </section>

      <!-- References -->
      <section class="references section-box" id="references">
        <article class="entity-primary-details">
          <div class="section-header">
            <h4 class="descriptor body-large">References</h4>
          </div>

          <app-article-references></app-article-references>
          <app-media-references [class.section-disabled]="this.incidentsPageStore.isPending()"></app-media-references>
          <app-master-duplicate-references [user]="user"></app-master-duplicate-references>
        </article>
      </section>
    </form>
  } @else {
    <app-merge-view (formFieldMerged)="updateFormGroup(true)"></app-merge-view>
  }
} @else {
  Page loading
}
